import { useState, type FormEvent } from "react";
import { siteInfo } from "@/mocks/site";
import { useSeo } from "@/hooks/useSeo";
import Breadcrumb from "@/components/base/Breadcrumb";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

const SUBMIT_ADDR = "https://readdy.ai/api/form/db73thod6mcdj80oo7e0";

export default function ContactPage() {
  useSeo({
    title: "联系方式 - 风间由美官方网站",
    description:
      "风间由美官方网站联系方式页，提供官方邮箱、媒体合作邮箱与社交媒体账号，并可通过反馈表单与我们取得联系。",
    keywords: "风间由美联系方式,风间由美官方邮箱,媒体合作,反馈表单",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(String(serverMsg || "提交失败，请稍后重试。"));
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，暂时无法提交，请稍后再试。");
    }
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb items={[{ label: "首页", path: "/" }, { label: "联系方式" }]} />

      <header className="mt-6 border-b border-background-200 pb-8">
        <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground-950 md:text-4xl">
          联系方式
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
          如需媒体采访、品牌合作或向我们提供资讯线索，欢迎通过以下官方渠道联系风间由美工作室。我们通常会在三个工作日内回复。
        </p>
      </header>

      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-5">
          <div className="flex flex-col gap-5">
            <div className="rounded-lg border border-background-200 bg-background-100/70 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-mail-send-line text-primary-500" />
                官方联络邮箱
              </h2>
              <ul className="mt-4 space-y-4 text-sm">
                <li>
                  <p className="text-xs text-foreground-500">粉丝来信 / 综合咨询</p>
                  <p className="mt-1 break-all font-medium text-foreground-900">{siteInfo.email}</p>
                </li>
                <li>
                  <p className="text-xs text-foreground-500">媒体与商务合作</p>
                  <p className="mt-1 break-all font-medium text-foreground-900">
                    {siteInfo.pressEmail}
                  </p>
                </li>
                <li>
                  <p className="text-xs text-foreground-500">办公方式</p>
                  <p className="mt-1 font-medium text-foreground-900">{siteInfo.address}</p>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                <i className="ri-share-circle-line text-primary-500" />
                社交媒体
              </h2>
              <ul className="mt-4 flex flex-col gap-3">
                {siteInfo.socials.map((s) => (
                  <li
                    key={s.name}
                    className="flex items-center gap-3 rounded-md bg-background-100/70 px-4 py-3"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className={`${s.icon} text-lg`} />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground-900">{s.name}</span>
                      <span className="block text-xs text-foreground-500">{s.handle}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <p className="mt-4 flex items-start gap-2 text-xs text-foreground-400">
                <i className="ri-information-line mt-0.5" />
                以上社交媒体账号为示意展示，暂不提供跳转与验证。
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">留言反馈</h2>
            <p className="mt-2 text-sm text-foreground-600">
              请填写下方表单，我们会认真阅读每一条留言。带 <span className="text-primary-600">*</span> 的为必填项。
            </p>

            <form
              id="kzm-contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-6 flex flex-col gap-5"
            >
              <div className="flex flex-col gap-2">
                <label htmlFor="contact-name" className="text-sm font-medium text-foreground-800">
                  姓名 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  maxLength={50}
                  placeholder="请输入你的称呼"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-email" className="text-sm font-medium text-foreground-800">
                  邮箱 <span className="text-primary-600">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收我们的回复"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-topic" className="text-sm font-medium text-foreground-800">
                  留言类型
                </label>
                <select
                  id="contact-topic"
                  name="topic"
                  defaultValue="粉丝来信"
                  className="w-full cursor-pointer rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                >
                  <option value="粉丝来信">粉丝来信</option>
                  <option value="媒体采访">媒体采访</option>
                  <option value="品牌合作">品牌合作</option>
                  <option value="资讯线索">资讯线索</option>
                  <option value="其他建议">其他建议</option>
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label htmlFor="contact-message" className="text-sm font-medium text-foreground-800">
                  留言内容 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={6}
                  placeholder="请输入你想对风间由美说的话（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                />
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="form-extra-note"
              />

              <div className="flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-line" />
                  {status === "submitting" ? "提交中..." : "提交留言"}
                </button>
                <span className="text-xs text-foreground-400">
                  我们尊重你的隐私，邮箱仅用于回复本次留言。
                </span>
              </div>

              {status === "success" ? (
                <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-checkbox-circle-line mt-0.5 text-base" />
                  感谢你的留言，我们已收到，会尽快与您联系。
                </div>
              ) : null}

              {status === "error" ? (
                <div className="flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                  <i className="ri-error-warning-line mt-0.5 text-base" />
                  {formError}
                </div>
              ) : null}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}